import cn from 'classnames'
import React from 'react'
import { escapedNewLineToLineBreakTag } from '../src/utils/escapedNewLineToLineBreakTag'
import { useLocalization } from '../src/hooks/useLocaleFormatter'
import { VendorExtendedFragment } from '../src/model/fragments'
import { getLocale } from '../src/model/locale'
import { isDefined } from '../src/utils/isDefined'
import { OpeningHours } from './OpeningHours'
import { PersonCard } from '../pages/contactMap'
import { Documents } from './Documents'
import { GalleryMasonry } from './GalleryMasonry'
import { VideoList } from './VideoList'
import { ProductRackList } from './ProductRackList'
import { CardDocument } from './CardDocument'
import SvgModel3d from './icons/SvgModel3d'
import { VideoTiles } from './VideoTiles'
import { Spacer } from './Spacer'
import { PhoneNumbers } from './PhoneNumbers'

export interface VendorLayoutProps {
	vendor: VendorExtendedFragment
}

export const VendorLayout: React.FunctionComponent<VendorLayoutProps> = ({
	vendor
}) => {
	const { translate, possibleLocales } = useLocalization()
	const data = getLocale(vendor.locales, possibleLocales)
	if (!data) {
		return <></>
	}

	const galleryItems = (vendor.gallery?.items || [])
			.map((x) => x.image)
			.filter(isDefined)

	return <div className={cn('vendorLayout')}>
		{
			vendor.logo &&
			<Spacer initial={10} />
		}

		{/* ANNOUNCEMENT /////////////////////////////////////////////////////////////// */}
		{ data.announcement && 
			<div className="vendorLayout-announcement">
				<strong>{escapedNewLineToLineBreakTag(data.announcement)}</strong>
			</div>
		}

		{/* CONTACT /////////////////////////////////////////////////////////////////// */}
		<div className="vendorLayout-contact">
			<div className='vendorLayout-contact-info'>
				<address>
					{escapedNewLineToLineBreakTag(vendor.address)}, {vendor.zip} {vendor.city} 
				</address>

				<div className='vendorLayout-contact-info-table'>
					{
						vendor.website && <>
							<div>Web: </div>
							<div>
								<a className="" href={vendor.website.indexOf('://') == -1 ? `https://${vendor.website}` : `${vendor.website}`} target="_blank" rel="noopener">
									{vendor.website.replace(/^[a-z]+:\/\//, '')}
								</a>
							</div>
						</>
					}
					{
						vendor.phone && <>
							<div>Tel: </div>
							<div><PhoneNumbers numbers={vendor.phone} /></div>
						</>
					}
					{
						vendor.email && <>
							<div>Email: </div>
							<div>
								<a href={`mailto:${vendor.email}`}>{vendor.email}</a>
							</div>
						</>
					}
				</div>
				
			</div>
			{
				(vendor.salesRepresentative || vendor.panoramaUrl || vendor.situationPlan) &&
				<div className="vendorLayout-contact-left">
					{vendor.salesRepresentative && (
						<div className="vendorLayout-contact-salesRepresentative">
								<PersonCard
									forcePosition={translate('vendor-sales-representative')}
									person={vendor.salesRepresentative}
									isActive={true}
								/>
						</div>
					)}
					{vendor.panoramaUrl &&
						<div className='vendorLayout-panorama'>
							<a target="_blank" href={vendor.panoramaUrl}>
								<SvgModel3d />
								<span>{ translate('3d-virtualni-prohlidka') }</span>
							</a>
						</div>
					}
					{vendor.situationPlan &&
						<CardDocument item={vendor.situationPlan} forcedTitle={translate('vendor-situation-plan')} />
					}
				</div>
			}
			{vendor.openingHours.length > 0 && <div className="vendorLayout-contact-openingHours">
					<OpeningHours
						data={vendor.openingHours}
						note={
							data.openingHoursInfo && <strong>{escapedNewLineToLineBreakTag(data.openingHoursInfo)}</strong>
						}
					/>
				</div>
			}
		</div>
		
		{/* PRODUCT RACKS /////////////////////////////////////////////////////////////// */}
		{vendor.productRacks.length > 0 && (
			<section className="vendorLayout-productRacks">
				<h2>{translate('productRacks-label')}</h2>
				<ProductRackList racks={vendor.productRacks} mode='products' />
			</section>
		)}

		{/* VIDEOS /////////////////////////////////////////////////////////////// */}
		{data.videos.length > 0 && (
			<section className="vendorLayout-videos">
				<h2>{ translate('videos-label') }</h2>
				<VideoList items={data.videos.slice(0, 1)} />
				{
					data.videos.length > 1 && (<>
						<Spacer initial={20} />
						<VideoTiles items={data.videos.slice(1).map(v => ({
							...v,
							image: undefined
						}))} />
					</>)
				}
			</section>
		)}

		{/* DOCUMENTS /////////////////////////////////////////////////////////////// */}
		{vendor.documents.items.length > 0 && <section className='vendorLayout-documents'>
				<h2>{translate('documents-label')}</h2>
				<Documents
					mode='simple'
					items={vendor.documents.items
						.map((x) => x.document)
						.filter(isDefined)}
				/>
			</section>
		}

		{/* GALLERY /////////////////////////////////////////////////////////////// */}
		{galleryItems.length > 0 && (
				<section className="vendorLayout-gallery">
					<h2>{ translate('gallery-label') }</h2>
					<GalleryMasonry
						items={galleryItems.map((image, i) => ({
							id: `${i}`,
							image,
							name: '',
						}))}
					/>
				</section>
			)
	}
	</div>;
}
		